import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { IMG } from "@/data/site";

const STATS = [
  { value: "20+", label: "Years of craft" },
  { value: "3,000+", label: "Covers sewn" },
  { value: "100%", label: "Made to measure" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO title="About the Maker" description="The story behind Tanonshop — over 20 years of handcraftsmanship creating made-to-measure custom cushion covers." />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-16 lg:pt-28">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-end">
          <Reveal>
            <Eyebrow>Our story</Eyebrow>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.03]">
              A craft measured in decades, not deadlines.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-lg text-teal-anchor/70 leading-relaxed font-light">
              Tanonshop is a one-maker workshop built on a simple belief: a cushion cover should fit
              like it was born on the frame. For over twenty years, that standard has never moved.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-8">
        <Reveal className="aspect-[16/8] overflow-hidden">
          <img src={IMG.sewing} alt="The maker sewing custom fabric" className="h-full w-full object-cover" />
        </Reveal>
      </section>

      <section className="mx-auto max-w-3xl px-5 sm:px-8 py-20 lg:py-28">
        <div className="space-y-10 text-lg text-teal-anchor/80 leading-relaxed">
          <Reveal as="p">
            It started with a single family heirloom — a worn armchair that deserved better than a
            factory replacement. One careful reupholstery turned into a request from a neighbor, then
            a friend, then a stranger with an oddly-shaped bay window seat nobody else would touch.
          </Reveal>
          <Reveal as="p">
            Two decades later, the workbench looks much the same: sharp shears, a trusted machine,
            rolls of upholstery, marine and outdoor fabric, and a tape measure that has seen thousands
            of projects. What hasn't changed is the promise behind every cover — measured by hand,
            cut with intention, and finished so the seams disappear into the design.
          </Reveal>
          <Reveal as="blockquote" className="border-l-2 border-teal-primary pl-6 font-serif text-2xl lg:text-3xl italic tracking-tight text-teal-anchor">
            "I don't sell cushions off a shelf. I build the one that belongs in your room, your camper,
            your boat — and nowhere else."
          </Reveal>
          <Reveal as="p">
            From full living-room sets to a lone RV dinette bench, every project gets the same attention.
            That's why so much of the work today comes from referrals and repeat customers who came back
            years later for the next room.
          </Reveal>
        </div>
      </section>

      <section className="bg-teal-anchor grain relative">
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <div className="grid gap-10 sm:grid-cols-3">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.1} className="text-center sm:text-left">
                <p className="font-serif text-5xl lg:text-6xl tracking-tight text-paper">{s.value}</p>
                <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-paper/60">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 sm:px-8 py-24 text-center">
        <h2 className="font-serif text-3xl lg:text-5xl tracking-tight">Let's make something that lasts.</h2>
        <Link to="/custom-order" className="mt-8 inline-flex items-center gap-2 rounded-sm bg-teal-primary px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-paper transition-all duration-200 hover:bg-teal-hover hover:-translate-y-px">
          Request a Custom Quote <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
}
